import { OrbitControls } from "@react-three/drei";
import { useExperienceStore } from "@/stores/useExperienceStore";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useShallow } from "zustand/shallow";
import { useFilterIsShaken } from "@/hooks/useFilter";
import { folder, useControls } from "leva";
import type { OrbitControls as OrbitControlsType } from "three-stdlib";
import { useGsapOrbitPosition } from "@/features/animations/hooks/factories/useGsapOrbit";
import { EaseOptions } from "@/features/animations/core/index.types";
import { useResponsive } from "@/hooks/useResponsive";

const folderName = "Camera & Controls";
export function Controls() {
  const [isShaken] = useFilterIsShaken();
  const controlsRef = useRef(null);
  const { orbitControls, pre, post, setPre, setPost, setOrbitControls } = useExperienceStore(useShallow((state) => ({ orbitControls: state.orbitControls, pre: state.pre, post: state.post, setPre: state.setPre, setPost: state.setPost, setOrbitControls: state.setOrbitControls }))); //prettier-ignore

  const [shakenCtrl, setShakenCtrl] = useControls(
    () => ({
      [folderName]: folder(
        {
          shaken: {
            value: isShaken,
            onChange: () => {}, 
            onEditEnd: (v: boolean) => {
              if (!orbitControls) return;
              console.log("editEnd")
              const targetControls = v ? post.controls : pre.controls;
              Object.assign(orbitControls, targetControls);
              orbitControls.update();
            },
            transient: false,
          },
        },
        { collapsed: true }
      ),
    }),
    [orbitControls, post, pre, isShaken]
  );

  useEffect(() => setShakenCtrl({shaken: isShaken}), [isShaken, setShakenCtrl])

  const preShakeControls = useControls(
    folderName,
    {
      preShake: folder(
        {
          minPolarAngle: {
            value: pre.controls.minPolarAngle,
            min: 0,
            max: Math.PI,
            step: Math.PI * 0.01,
            onEditEnd: (v) => setPre({ controls: { ...pre.controls, minPolarAngle: v } }),
          },
          maxPolarAngle: {
            value: pre.controls.maxPolarAngle,
            min: 0,
            max: Math.PI,
            step: Math.PI * 0.01,
            onEditEnd: (v) => setPre({ controls: { ...pre.controls, maxPolarAngle: v } }),
          },
          minAzimuthAngle: {
            value: pre.controls.minAzimuthAngle,
            min: 0.01,
            max: Math.PI * 2,
            step: Math.PI * 0.01,
            onEditEnd: (v) => setPre({ controls: { ...pre.controls, minAzimuthAngle: v } }),
          },
          maxAzimuthAngle: {
            value: pre.controls.maxAzimuthAngle,
            min: 0.01,
            max: Math.PI * 2,
            step: Math.PI * 0.01,
            onEditEnd: (v) => setPre({ controls: { ...pre.controls, maxAzimuthAngle: v } }),
          },
          minDistance: {
            value: pre.controls.minDistance,
            min: 0,
            max: 10,
            onEditEnd: (v) => setPre({ controls: { ...pre.controls, minDistance: v } }),
          },
          maxDistance: {
            value: pre.controls.maxDistance,
            min: -10,
            max: 10,
            onEditEnd: (v) => setPre({ controls: { ...pre.controls, maxDistance: v } }),
          },
          autoRotate: false,
          autoRotateSpeed: { value: 2, min: -10, max: 10 },
        },
        { collapsed: true }
      ),
    },
    { collapsed: true },
    [orbitControls, pre, setPre]
  );

  const postShakeControls = useControls(
    folderName,
    {
      postShake: folder(
        {
          minPolarAngle: {
            value: post.controls.minPolarAngle,
            min: 0.01,
            max: Math.PI,
            step: Math.PI * 0.01,
            onEditEnd: (v) => setPost({ controls: { ...post.controls, minPolarAngle: v } }),
          },
          maxPolarAngle: {
            value: post.controls.maxPolarAngle,
            min: 0,
            max: Math.PI,
            step: Math.PI * 0.01,
            onEditEnd: (v) => setPost({ controls: { ...post.controls, maxPolarAngle: v } }),
          },
          minAzimuthAngle: {
            value: post.controls.minAzimuthAngle,
            min: 0.01,
            max: Math.PI * 2,
            step: Math.PI * 0.01,
            onEditEnd: (v) => setPost({ controls: { ...post.controls, minAzimuthAngle: v } }),
          },
          maxAzimuthAngle: {
            value: post.controls.maxAzimuthAngle,
            min: 0,
            max: Math.PI * 2,
            step: Math.PI * 0.01,
            onEditEnd: (v) => setPost({ controls: { ...post.controls, maxAzimuthAngle: v } }),
          },
          minDistance: {
            value: post.controls.minDistance,
            min: 0,
            max: 10,
            onEditEnd: (v) => setPost({ controls: { ...post.controls, minDistance: v } }),
          },
          maxDistance: {
            value: post.controls.maxDistance,
            min: -10,
            max: 10,
            onEditEnd: (v) => setPost({ controls: { ...post.controls, maxDistance: v } }),
          },
          autoRotate: true,
          autoRotateSpeed: { value: 1.5, min: -10, max: 10 },
        },
        { collapsed: true }
      ),
    },
    { collapsed: true },
    [orbitControls, post, setPost]
  );

  // initial stuff (add control refs to store and adjust camera)
   useEffect(() => { if (controlsRef && controlsRef.current) setOrbitControls(controlsRef.current) }, [controlsRef, setOrbitControls]); //prettier-ignore


   useResize(orbitControls, shakenCtrl.shaken)    // adjust position to ensure it matches zoom in effect adjustment
   useAdjustCamera(orbitControls, shakenCtrl.shaken); // initial zoom in effect adjustment

  return (
    <>
      <OrbitControls
        ref={controlsRef}
        makeDefault={true}
        enableZoom={true}
        enableDamping={true}
        enablePan={false}
        // state based
        autoRotate={shakenCtrl.shaken ? postShakeControls.autoRotate : preShakeControls.autoRotate} //prettier-ignore
        autoRotateSpeed={shakenCtrl.shaken ? postShakeControls.autoRotateSpeed : preShakeControls.autoRotateSpeed} //prettier-ignore
      />
    </>
  );
}

//prettier-ignore
function useAdjustCamera(orbitControls: OrbitControlsType | null , state: boolean) 
{
  const adjustPosition = useGsapOrbitPosition()

  // init effect and runs once ( when the orbitControls has been init)
  useEffect(() => {
    if (!orbitControls) return;

    const { pre: currentPre, post: currentPost } = useExperienceStore.getState(); // ensure we dont have stale values

    Object.assign(orbitControls, {
      ...currentPre.controls,
      minDistance: 7,
      maxDistance: 7,
    });

    orbitControls.update();

    const t = adjustPosition(orbitControls, { duration: 1.5, ease: EaseOptions.Power1InOut, position: currentPre.controls.maxDistance}).invalidate().play()
    t.then(() => { Object.assign(orbitControls, state ? {...currentPost.controls} : {...currentPre.controls}); orbitControls.update() })
    
    return () => {
      t.revert();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orbitControls]);

}

function useResize(orbitControls: OrbitControlsType | null , state: boolean) {

  const { isMobile, isTablet, isDesktop } = useResponsive()

  // breakpoints
  const controls = useControls(folderName, {
    responsive: folder({
      sm: folder({                                                                                                            
        sm_distance: { value: 3, min: 0, max: 10, step: 0.01, label: 'distance' },
        sm_positionY: { value: 0.25, min: -2, max: 2, step: 0.01, label: 'positionY' },                                        
      }, {collapsed: true}),                                                                                                
      md: folder({
        md_distance: { value: 3, min: 0, max: 10, step: 0.01, label: 'distance' },
        md_positionY: { value: 0.1, min: -2, max: 2, step: 0.01, label: 'positionY' },
      }, {collapsed: true}),
      lg: folder({
        lg_distance: { value: 2.5, min: 0, max: 10, step: 0.01, label: 'distance' },
        lg_positionY: { value: 0,   min: -2, max: 2, step: 0.01, label: 'positionY' },
      }, {collapsed: true}),
    })
  }, [])

    const syncDistance = useCallback((state: boolean) => {    
    if (!orbitControls) return;      

      const distance = isMobile ? controls.sm_distance : isTablet ? controls.md_distance : controls.lg_distance 
      const y = isMobile ? controls.sm_positionY : isTablet ? controls.md_positionY : controls.lg_positionY 
  
      useExperienceStore.setState(s => ({                                                                                     
        pre:  { ...s.pre,  controls: { ...s.pre.controls, minDistance: distance, maxDistance: distance } },                          
        post: { ...s.post, controls: { ...s.post.controls, minDistance: distance, maxDistance: distance * 4 } },
        scenePosition: { ...s.scenePosition, y: y },
      }));

    
    const { pre: currentPre, post: currentPost } = useExperienceStore.getState(); // ensure we dont have stale values
    
    Object.assign(orbitControls, state ? {...currentPost.controls} : {...currentPre.controls}); 
    orbitControls.update()

    }, [isMobile, isTablet, orbitControls, controls])


/** Sync effect
 *  Updates the stores camera control values. 
 *  We need a function that reacts to this this and actually updates the camera position.
 */

// Layout effect to ensure it runs before the useAdjustCamera
useLayoutEffect(() => {   
  if (!orbitControls) return;        
    
  const handler = () => syncDistance(state); 
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [JSON.stringify(controls), orbitControls, syncDistance, state]);

// run it once to sync init
useLayoutEffect(() =>  syncDistance(state), [syncDistance])

}